import { AwardIcon, CoinsIcon, PodiumIcon, RelaxIcon } from '@icons'
import classnames from 'classnames'
import { m, LazyMotion, domAnimation, useAnimation } from 'framer-motion'
import { useCallback, useEffect, useMemo, useState } from 'react'

import styles from './styles.module.scss'

type UniqueIngredientsCardProps = {
  className?: string
}

const PROGRESS_BAR_DURATION = 10 // in seconds
const PROGRESS_BAR_INTERVAL_DELAY = 0.5 // in seconds

const PROGRESS_VARIANTS = {
  start: { width: '0%', transition: { duration: PROGRESS_BAR_DURATION } },
  end: { width: '100%', transition: { duration: PROGRESS_BAR_DURATION } },
}

const DESCRIPTION_VARIANTS = {
  start: {
    opacity: 0.01,
    y: 0,
    transition: { duration: 0.5 },
    transitionEnd: { display: 'none' },
  },
  end: {
    opacity: 1,
    y: -10,
    display: 'block',
    transition: { duration: 0.5 },
  },
}

const MotionCoinsIcon = m(CoinsIcon)
const MotionRelaxIcon = m(RelaxIcon)
const MotionAwardIcon = m(AwardIcon)
const MotionPodiumIcon = m(PodiumIcon)

const ICON_VARIANTS = {
  start: { opacity: 0.01, y: 0, transition: { duration: 0.5 } },
  end: { opacity: 1, y: -10, transition: { duration: 0.5 } },
}

const ROCKET_EARN_SECTION_ID = 'rocketEarn'
const ROCKET_AUTONOMY_SECTION_ID = 'rocketAutonomy'
const ROCKET_LONG_TERM_SECTION_ID = 'rocketLongTerm'
const ROCKET_CAREER_BOOSTER_SECTION_ID = 'rocketCareerBooster'

const SECTION_IDS = {
  [ROCKET_EARN_SECTION_ID]: 0,
  [ROCKET_AUTONOMY_SECTION_ID]: 1,
  [ROCKET_LONG_TERM_SECTION_ID]: 2,
  [ROCKET_CAREER_BOOSTER_SECTION_ID]: 3,
}

type SectionIdsType = keyof typeof SECTION_IDS

export const UniqueIngredientsCard = ({ className }: UniqueIngredientsCardProps) => {
  const progressBarOneControl = useAnimation()
  const progressBarTwoControl = useAnimation()
  const progressBarThreeControl = useAnimation()
  const progressBarFourControl = useAnimation()
  const descriptionOneControl = useAnimation()
  const descriptionTwoControl = useAnimation()
  const descriptionThreeControl = useAnimation()
  const descriptionFourControl = useAnimation()
  const iconControl = useAnimation()

  const [triggerAnimation, setTriggerAnimation] = useState(false)
  const [displaySection, setDisplaySection] = useState(SECTION_IDS[ROCKET_EARN_SECTION_ID])
  const [sectionInterval, setSectionInterval] = useState<NodeJS.Timer>()

  const stopAllAnimations = useCallback(() => {
    progressBarOneControl.stop()
    descriptionOneControl.stop()
    progressBarOneControl.set('start')
    descriptionOneControl.set('start')

    progressBarTwoControl.stop()
    descriptionTwoControl.stop()
    progressBarTwoControl.set('start')
    descriptionTwoControl.set('start')

    progressBarThreeControl.stop()
    descriptionThreeControl.stop()
    progressBarThreeControl.set('start')
    descriptionThreeControl.set('start')

    progressBarFourControl.stop()
    descriptionFourControl.stop()
    progressBarFourControl.set('start')
    descriptionFourControl.set('start')
  }, [
    descriptionFourControl,
    descriptionOneControl,
    descriptionThreeControl,
    descriptionTwoControl,
    progressBarFourControl,
    progressBarOneControl,
    progressBarThreeControl,
    progressBarTwoControl,
  ])

  const animations = useMemo(
    () => ({
      [SECTION_IDS[ROCKET_EARN_SECTION_ID]]: () => {
        stopAllAnimations()
        descriptionOneControl.start('end')
        progressBarOneControl.start('end')
      },

      [SECTION_IDS[ROCKET_AUTONOMY_SECTION_ID]]: () => {
        stopAllAnimations()
        descriptionTwoControl.start('end')
        progressBarTwoControl.start('end')
      },

      [SECTION_IDS[ROCKET_LONG_TERM_SECTION_ID]]: () => {
        stopAllAnimations()
        descriptionThreeControl.start('end')
        progressBarThreeControl.start('end')
      },

      [SECTION_IDS[ROCKET_CAREER_BOOSTER_SECTION_ID]]: () => {
        stopAllAnimations()
        descriptionFourControl.start('end')
        progressBarFourControl.start('end')
      },
    }),
    [
      descriptionFourControl,
      descriptionOneControl,
      descriptionThreeControl,
      descriptionTwoControl,
      progressBarFourControl,
      progressBarOneControl,
      progressBarThreeControl,
      progressBarTwoControl,
      stopAllAnimations,
    ]
  )

  const descriptionListItems = useMemo(
    () => [
      {
        animateProgressBar: progressBarOneControl,
        title: 'Keep 100% of what you earn',
        description:
          'Set your rates and keep 100% of what you make while doing what you love. No asterisks, no fine print.',
        animateDescription: descriptionOneControl,
        id: ROCKET_EARN_SECTION_ID,
      },
      {
        animateProgressBar: progressBarTwoControl,
        title: 'High autonomy, high flexibility',
        description:
          'Work from anywhere and get paid in USD to spend it using our debit card or transfer it to your local bank account.',
        animateDescription: descriptionTwoControl,
        id: ROCKET_AUTONOMY_SECTION_ID,
      },
      {
        animateProgressBar: progressBarThreeControl,
        title: 'Long-term meaningful work',
        description:
          'Work on long-term projects that make sense based on your skill set, personal values, and interest.',
        animateDescription: descriptionThreeControl,
        id: ROCKET_LONG_TERM_SECTION_ID,
      },
      {
        animateProgressBar: progressBarFourControl,
        title: 'Career booster',
        description:
          'Supported by our community, training, and learning experiences, you will fly higher than ever imagined.',
        animateDescription: descriptionFourControl,
        id: ROCKET_CAREER_BOOSTER_SECTION_ID,
      },
    ],
    [
      descriptionFourControl,
      descriptionOneControl,
      descriptionThreeControl,
      descriptionTwoControl,
      progressBarFourControl,
      progressBarOneControl,
      progressBarThreeControl,
      progressBarTwoControl,
    ]
  )

  const triggerSectionAnimation = useCallback(async () => {
    animations[displaySection]()
  }, [displaySection, animations])

  const handleIntervalTrigger = useCallback(() => {
    setDisplaySection(previousValue => {
      if (previousValue === SECTION_IDS[ROCKET_CAREER_BOOSTER_SECTION_ID])
        return SECTION_IDS[ROCKET_EARN_SECTION_ID]

      return previousValue + 1
    })
  }, [])

  const handleSectionClick = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      const sectionId = event.currentTarget.id
      if (sectionInterval) {
        clearInterval(sectionInterval)
      }
      const newInterval = setInterval(
        handleIntervalTrigger,
        (PROGRESS_BAR_DURATION + PROGRESS_BAR_INTERVAL_DELAY) * 1000
      )
      setSectionInterval(newInterval)
      setDisplaySection(SECTION_IDS[sectionId as SectionIdsType])
    },
    [sectionInterval, handleIntervalTrigger, setSectionInterval, setDisplaySection]
  )

  useEffect(() => {
    if (triggerAnimation) {
      triggerSectionAnimation()
    }
  }, [triggerSectionAnimation, triggerAnimation, displaySection])

  useEffect(() => {
    if (triggerAnimation && !sectionInterval) {
      if (sectionInterval) {
        clearInterval(sectionInterval)
      }
      const newInterval = setInterval(
        handleIntervalTrigger,
        (PROGRESS_BAR_DURATION + PROGRESS_BAR_INTERVAL_DELAY) * 1000
      )
      setSectionInterval(newInterval)
      iconControl.start('end')
    }

    return () => {
      if (sectionInterval) {
        clearInterval(sectionInterval)
      }
    }
  }, [iconControl, triggerAnimation, sectionInterval, handleIntervalTrigger])

  const icon = useMemo(() => {
    switch (displaySection) {
      case SECTION_IDS[ROCKET_EARN_SECTION_ID]:
        return (
          <MotionCoinsIcon
            className={styles['ingredients-icon']}
            variants={ICON_VARIANTS}
            initial="start"
            animate="end"
            exit="start"
          />
        )
      case SECTION_IDS[ROCKET_AUTONOMY_SECTION_ID]:
        return (
          <MotionRelaxIcon
            className={styles['ingredients-icon']}
            variants={ICON_VARIANTS}
            initial="start"
            animate="end"
            exit="start"
          />
        )
      case SECTION_IDS[ROCKET_LONG_TERM_SECTION_ID]:
        return (
          <MotionAwardIcon
            className={styles['ingredients-icon']}
            variants={ICON_VARIANTS}
            initial="start"
            animate="end"
            exit="start"
          />
        )
      default:
        return (
          <MotionPodiumIcon
            className={styles['ingredients-icon']}
            variants={ICON_VARIANTS}
            initial="start"
            animate="end"
            exit="start"
          />
        )
    }
  }, [displaySection])

  const onEnterView = useCallback(() => {
    setTriggerAnimation(true)
  }, [])

  return (
    <LazyMotion features={domAnimation} strict>
      <m.div className={classnames(styles['container'], className)} onViewportEnter={onEnterView}>
        <p className={styles['title']}>The ingredients that make us unique:</p>
        <div className={styles['content']}>
          {icon}
          <dl className={styles['description-list']}>
            {descriptionListItems.map(item => (
              <div key={item.title}>
                <div className={styles['progress-container']}>
                  <div className={styles['progress']} />
                  <m.div
                    className={styles['progress-done']}
                    variants={PROGRESS_VARIANTS}
                    animate={item.animateProgressBar}
                    initial="start"
                  />
                </div>
                <dt
                  role="button"
                  onClick={handleSectionClick}
                  id={item.id}
                  className={styles['description-title']}
                >
                  {item.title}
                </dt>
                <m.dd
                  className={styles['description']}
                  variants={DESCRIPTION_VARIANTS}
                  animate={item.animateDescription}
                  initial="start"
                >
                  {item.description}
                </m.dd>
              </div>
            ))}
          </dl>
        </div>
      </m.div>
    </LazyMotion>
  )
}

export default UniqueIngredientsCard
